import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { bookingApi, unwrap } from '@/lib/api/client';

/**
 * When things happen, by day and hour.
 *
 * From docs/booking-api.swagger.json: `HeatMapModel` in, `HoursHeatMapDto` out.
 * The two heatmaps — sales and bookings — take the same body and answer the
 * same shape; only the endpoint differs, so they are one page parameterised.
 */

export type HeatmapKind = 'sales' | 'bookings';

export interface HeatmapModel {
  eventType: number | null;
  startDate: string;
  endDate: string;
}

/** `HeatMapBookingEventDetail` — what was on in that hour. */
export interface HeatmapEvent {
  eventStartDate: string | null;
  eventEndDate: string | null;
  eventName: string | null;
}

/** `HeatMapBookingHourDetail`. */
export interface HeatmapHour {
  startDate: string | null;
  endDate: string | null;
  /** The hour's label, already formatted by the API. */
  name: string | null;
  count: number | null;
  events: HeatmapEvent[] | null;
}

/** `HeatMapBookingDayDetail`. */
export interface HeatmapDay {
  date: string | null;
  dateString: string | null;
  hours: HeatmapHour[] | null;
}

/** `HoursHeatMapDto`. The scale comes from the API, not from the page. */
export interface Heatmap {
  minValue: number | null;
  maxValue: number | null;
  days: HeatmapDay[] | null;
}

export function useHeatmap(kind: HeatmapKind, model: HeatmapModel) {
  return useQuery({
    queryKey: ['heatmap', kind, model],
    queryFn: () =>
      unwrap<Heatmap>(bookingApi.post(`admin/hours/heatmap/${kind}`, model)),
    placeholderData: keepPreviousData,
  });
}
